import { debounce, fetchConfig } from '@theme/utilities';

class CartOrderComments extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Promise<void> | null} */
  #pendingUpdate = null;

  #hasUnsavedChange = false;

  #latestComments = '';

  connectedCallback() {
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('click', this.#handleCheckoutClick);
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('click', this.#handleCheckoutClick);
  }

  #handleInput = (event) => {
    if (!(event.target instanceof HTMLTextAreaElement) || event.target.name !== 'attributes[Order Comments]') return;

    this.#syncFields(event.target.value);
    this.#latestComments = event.target.value;
    this.#hasUnsavedChange = true;
    this.#queueCommentsUpdate();
  };

  #handleCheckoutClick = async (event) => {
    const checkoutLink = event.target instanceof Element ? event.target.closest('a[href*="/checkout"]') : null;
    if (!checkoutLink || (!this.#pendingUpdate && !this.#hasUnsavedChange)) return;

    event.preventDefault();

    if (this.#pendingUpdate) {
      await this.#pendingUpdate;
    }

    if (this.#hasUnsavedChange) {
      await this.#saveLatestComments();
    }

    if (checkoutLink instanceof HTMLAnchorElement) {
      window.location.href = checkoutLink.href;
    }
  };

  #queueCommentsUpdate = debounce(() => {
    this.#saveLatestComments();
  }, 200);

  #saveLatestComments() {
    if (!this.#hasUnsavedChange) return Promise.resolve();

    const comments = this.#latestComments;
    this.#hasUnsavedChange = false;
    this.#pendingUpdate = this.#saveComments(comments).finally(() => {
      this.#pendingUpdate = null;
    });

    return this.#pendingUpdate;
  }

  async #saveComments(comments) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            'Order Comments': comments,
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  #syncFields(value) {
    document.querySelectorAll('textarea[name="attributes[Order Comments]"]').forEach((el) => {
      if (el instanceof HTMLTextAreaElement && el.value !== value) {
        el.value = value;
      }
    });
  }
}

if (!customElements.get('cart-order-comments')) {
  customElements.define('cart-order-comments', CartOrderComments);
}
